import { useEffect, useRef, useState } from "preact/hooks";
import Image from "apps/website/components/Image.tsx";
import { Picture, Source } from "apps/website/components/Picture.tsx";
import type { ComponentProps } from "preact";

interface ContainerBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

type ZoomType = "click" | "hover";

interface Props extends ComponentProps<typeof Image> {
  type: ZoomType;
  factor: number;
}

const clamp = (min: number, max: number, value: number) =>
  Math.min(Math.max(value, min), max);

const getBounds = (element?: Element | null): ContainerBounds | null => {
  if (!element) return null;

  const bounds = element.getBoundingClientRect();

  return {
    x: bounds.left,
    y: bounds.top,
    width: bounds.width,
    height: bounds.height,
  };
};

const getMousePositionFromEvent = (
  event: MouseEvent,
  bounds?: ContainerBounds | null,
): { x: number; y: number; isOutOfBounds?: boolean } => {
  if (!bounds) {
    return { x: 0, y: 0 };
  }

  const [x, y] = [event.clientX - bounds.x, event.clientY - bounds.y];

  const threshold = 20;
  const isOutOfBounds = x < -threshold || y < -threshold ||
    x > bounds.width + threshold || y > bounds.height + threshold;

  // Values larger than 0 increase mouse movement sensivity
  const boost = 0.1;

  return {
    x: clamp(0, bounds.width, -bounds.width * boost + x * (1 + boost * 2)),
    y: clamp(0, bounds.height, -bounds.height * boost + y * (1 + boost * 2)),
    isOutOfBounds,
  };
};

export function ZoomableImage({ type, factor, preload, ...imageProps }: Props) {
  const [isZoomedIn, setZoom] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const containerBounds = useRef<ContainerBounds | null>(null);

  const setPositionAndScale = (x: number, y: number, scale: number) => {
    const contentElement = contentRef.current;

    if (!contentElement) {
      return;
    }

    contentElement.style.transform = `scale(${scale}, ${scale}) translate3d(${
      -x / scale
    }px, ${-y / scale}px, 0)`;
  };

  const getContainerBounds = () =>
    containerBounds.current ?? getBounds(containerRef.current);

  const handleMouseOver = () => {
    setZoom(true);
  };

  const handleClick = (event: MouseEvent) => {
    setZoom(!isZoomedIn);

    if (!isZoomedIn) {
      const mousePosition = getMousePositionFromEvent(
        event,
        getContainerBounds(),
      );

      setPositionAndScale(mousePosition.x, mousePosition.y, factor);
    }
  };

  useEffect(() => {
    if (!isZoomedIn) {
      setPositionAndScale(0, 0, 1);
      containerBounds.current = null;
    }
  }, [isZoomedIn]);

  const handleClickOutside = (event: MouseEvent) => {
    if (!isZoomedIn) {
      return;
    }

    const isDescendant = containerRef.current && event.target &&
      containerRef.current.contains?.(event.target as Node);

    if (isDescendant) {
      return;
    }

    setZoom(false);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (!isZoomedIn) {
      return;
    }

    const mousePosition = getMousePositionFromEvent(
      event,
      getContainerBounds(),
    );

    if (type === "hover" && mousePosition.isOutOfBounds) {
      setZoom(false);
    } else {
      setPositionAndScale(mousePosition.x, mousePosition.y, factor);
    }
  };

  useEffect(() => {
    if (document) {
      document.addEventListener("mousemove", handleMouseMove);
      if (type === "click") {
        document.addEventListener("click", handleClickOutside);
      }
    }

    return () => {
      if (document) {
        document.removeEventListener("mousemove", handleMouseMove);
        if (type === "click") {
          document.removeEventListener("click", handleClickOutside);
        }
      }
    };
  });

  return (
    <div
      ref={containerRef}
      onMouseOver={type === "hover" ? handleMouseOver : undefined}
      onClick={type === "click" ? handleClick : undefined}
      class="w-full relative overflow-hidden"
    >
      <div
        ref={contentRef}
        style={{
          transformOrigin: "0 0",
          fontSize: 0,
        }}
      >
        <Picture class="w-full" preload={preload}>
          <Source
            media="(max-width: 1024px)"
            src={imageProps.src}
            width={imageProps.width / 2}
            height={(imageProps.height ?? imageProps.width) / 2}
          />

          <Source
            media="(min-width: 1025px)"
            src={imageProps.src}
            width={imageProps.width}
            height={imageProps.height ?? imageProps.width}
          />

          <img data-zoomed={isZoomedIn} {...imageProps} />
        </Picture>
      </div>
    </div>
  );
}
